import { Grid, Typography, useEventCallback } from '@mui/material';
import FilterAct from 'civil-status/components/research-category/filter';
import TableAct from 'civil-status/components/research-category/table';
import { ActResearchCrumbs, CommunicationsResearchCrumbs } from 'civil-status/constants/breadcrumb';
import { transformerActCategoryList } from 'civil-status/utils/transformer-act-category-list';
import { transformerActResearch } from 'civil-status/utils/transformer-act-research';
import {
  ActListVM,
  ResearchAct,
  ResearchPerson,
} from 'civil-status/view-models/research-category-form';
import { Formik, Form } from 'formik';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ActCategory } from 'shell/models/act-category-list';
import { HttpResponse } from 'shell/models/http-response';
import { noop } from 'shell/utils/noop';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import TitleStep from 'civil-status/components/shared/title-step';

export const urlAct = `${__ACT_API_URL__}/consultazione/evento`;
export const urlEvent = `${__ACT_API_URL__}/consultazione/soggetto`;
export const urlOwner = `${__ACT_API_URL__}/consultazione/intestatario`;

export type FilterType = Record<
  string,
  Record<string, string | number | undefined> | string | undefined
>;

export type FilterTypeState = {
  url?: string;
  filter: FilterType;
};

export interface TypeFilterAct {
  act?: ResearchAct;
  person?: ResearchPerson;
  tableProps?: {
    page?: number;
    rowsPerPage?: number;
  };
}

interface ActResearchCatPageProps {
  isCommunications?: boolean;
}

const ActResearchCatPage = ({
  isCommunications = false,
}: ActResearchCatPageProps): ReactElement => {
  const params = useParams();

  const valueCat = params.valueCat || undefined;
  const idCat = params.idCat || undefined;

  const filterTable = useFilterTable<TypeFilterAct>();

  // filtri persona
  const [openFilterPerson, setOpenFilterPerson] = useState<boolean>(true);
  // filtri atto
  const [openFilterAct, setOpenFilterAct] = useState<boolean>(true);
  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<FilterTypeState>({
    url: '',
    filter: {},
  });
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const [actList, getActList] = useHttpFetch<HttpResponse<ActCategory>, Array<ActListVM>>(``, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerActCategoryList,
  });

  const getActListData = useEventCallback(
    (url: string, dataFilter?: FilterType, page?: number, rowsPerPage?: number) => {
      (
        getActList({
          url: url,
          data: {
            ...dataFilter,
            datiPaginazione: {
              numPagina: page || 0,
              numRecordPagina: rowsPerPage || 10,
            },
          },
        }) as unknown as ResponsePromise<void>
      ).then(() => {
        // salvo i filtri dell'ultima ricerca
        setFilterSelected({
          url,
          filter: dataFilter || {},
        });
      });
    }
  );

  const handleSubmitResearchAct = useEventCallback(
    (values: ResearchAct, page?: number, rowsPerPage?: number) => {
      // mantenere dati
      filterTable.setValues({
        act: values,
        person: undefined,
      });

      const dataFilter: FilterType = transformerActResearch(values, idCat);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getActListData(urlAct, dataFilter);
    }
  );

  const handleSubmitResearchPerson = useEventCallback(
    (values: ResearchPerson, page?: number, rowsPerPage?: number) => {
      // mantenere dati
      filterTable.setValues({ act: undefined, person: values });

      const dataFilter: FilterType = {
        criteriRicercaSoggetto: {
          codiceFiscale: values.fiscalCode || undefined,
          cognome: values.surname || undefined,
          nome: values.name || undefined,
          datanascita: values.birthDate ? values.birthDate.toFormat('yyyy-MM-dd') : undefined,
          sesso: values.gender || undefined,
          idComuneNascita: values.municipality?.code || undefined,
          nomeComuneNascita: values.municipality?.description || undefined,
          siglaProvinciaNascita: values.municipality?.province || undefined,
          codiceStatoNascita: values.state?.id || undefined,
          nomeStatoNascita: values.state?.value || undefined,
          idAnscSoggetto: values.idAnsc || undefined,
          idAnpr: values.idAnpr || undefined,
        },
      };

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getActListData(values.isEvent ? urlEvent : urlOwner, dataFilter);
    }
  );

  useEffect(() => {
    if (((actList.data as Array<ActListVM>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpenFilterPerson(false);
      setOpenFilterAct(false);
    }
  }, [actList.data]);

  useEffect(() => {
    if (filterTable.values?.act) {
      handleSubmitResearchAct(
        filterTable.values?.act,
        filterTable.values.tableProps?.page,
        filterTable.values.tableProps?.rowsPerPage
      );
    }

    if (filterTable.values?.person) {
      handleSubmitResearchPerson(
        filterTable.values?.person,
        filterTable.values.tableProps?.page,
        filterTable.values.tableProps?.rowsPerPage
      );
    }
  }, []);

  const setTablePropsFilterTable = useEventCallback((page?: number, rowsPerPage?: number) => {
    filterTable.setValues({
      ...filterTable.values,
      tableProps: {
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      },
    });
  });

  return (
    <Fragment>
      <Breadcrumbs
        items={isCommunications ? CommunicationsResearchCrumbs(valueCat, idCat) : ActResearchCrumbs}
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Formik initialValues={{}} onSubmit={noop}>
            <Form>
              <TitleStep
                onGetDataSaved={noop}
                getTextAct={noop}
                title={`Ricerca Atti ${valueCat || ''}`}
              />
            </Form>
          </Formik>
        </Grid>
        <div className="circle" />
        <Grid item xs={12}>
          <FilterAct
            openPerson={openFilterPerson}
            setOpenPerson={setOpenFilterPerson}
            openAct={openFilterAct}
            setOpenAct={setOpenFilterAct}
            idCat={idCat}
            handleSubmitResearchAct={handleSubmitResearchAct}
            handleSubmitResearchPerson={handleSubmitResearchPerson}
            initialValues={filterTable.values}
          />
          {actList.data ? (
            ((actList.data as Array<ActListVM>) || [])?.length > 0 ? (
              <>
                <TableAct
                  data={actList.data as Array<ActListVM>}
                  filterSelected={filterSelected}
                  setFilterSelected={setFilterSelected}
                  getActListData={getActListData}
                  idCat={idCat}
                  valueCat={valueCat}
                  tableProps={tableProps}
                  setTableProps={setTableProps}
                  onChoosePress={noop}
                  isCommunications={isCommunications}
                  setTablePropsFilterTable={setTablePropsFilterTable}
                />
              </>
            ) : (
              <Typography color="secondary.main">
                Nessun elemento trovato con la ricerca effettuata.
              </Typography>
            )
          ) : undefined}
        </Grid>
      </Grid>
    </Fragment>
  );
};

ActResearchCatPage.displayName = 'Act Research Cat Page component';

export default ActResearchCatPage;
